import { Select as SelectPrimitive } from '@base-ui/react/select';
import { useMemo } from 'react';

import type { PickerSize } from '../pickerPrimitiveStyles';
import SelectContext from './SelectContext';

type SelectValueType<Value, Multiple extends boolean | undefined> = Multiple extends true
  ? Value[]
  : Value;

export type SelectProps<
  Value,
  Multiple extends boolean | undefined = false,
> = SelectPrimitive.Root.Props<Value, Multiple> & {
  isLoading?: boolean;
  error?: Error | null;
  size?: PickerSize;
  onChange?: (value: SelectValueType<Value, Multiple> | null) => void;
};

const Select = <Value, Multiple extends boolean | undefined = false>({
  isLoading = false,
  error,
  size = 'large',
  children,
  items,
  value,
  isItemEqualToValue,
  multiple,
  ...props
}: SelectProps<Value, Multiple>) => {
  const handleValueChange: SelectPrimitive.Root.Props<Value, Multiple>['onValueChange'] = (
    newValue,
    eventDetails,
  ) => {
    props.onValueChange?.(newValue, eventDetails);
    props.onChange?.(newValue);
  };

  // If the value is not in the items, add it there. Otherwise the label will not be displayed.
  const itemsWithValue = useMemo(() => {
    if (!value || (Array.isArray(items) && (items as Value[]).length > 0)) {
      return items;
    }

    const selected = multiple ? (value as Value[]) : [value as Value];
    return selected as unknown as typeof items;
  }, [value, items, multiple]);

  return (
    <SelectContext value={{ size, isLoading, error }}>
      <SelectPrimitive.Root
        items={itemsWithValue}
        value={value}
        isItemEqualToValue={isItemEqualToValue}
        multiple={multiple}
        {...props}
        data-loading={isLoading ? 'true' : undefined}
        onValueChange={handleValueChange}
      >
        {children}
      </SelectPrimitive.Root>
    </SelectContext>
  );
};

export default Select;
